// The public site's runtime (ARCHITECTURE §2, §5.2). Mounted by App.tsx at
// `/*`, beside the standalone core pages and the admin:
//
//   - resolves the template (preview > dev override > published > classic);
//   - stamps <html data-site-template="<id>"> and the template's --st-* brand
//     variables (classic: none) and removes them on unmount, so admin-v2
//     never sees them; loads the template's fonts (classic: none), which stay
//     loaded (fonts.ts dedupes them);
//   - renders SITE_ROUTES through gates → PageRoute, and the catch-all
//     `*` → template.states.NotFound (#312);
//     a route with `onlyWith` (a strict add-on) is left out entirely while
//     that add-on is not entitled (or the tenant is unknown), so its URL
//     hits the catch-all as it always did, unless it names a `whenOff` page:
//     with the tenant known it then mounts on that page, not privateSite
//     'open' (owner audit, October 29: the member pages say "Registration
//     isn't available right now");
//   - provides the member session (MemberAuthProvider) to every page and
//     chrome; for a league without the registration add-on it is the inert
//     'off' value (no storage read, no request);
//   - decides the private site's wall (registration step 33): inside the
//     member session, SiteAccessProvider derives whether a members-only
//     league's visitor is admitted; while not (checking, walled,
//     unavailable) every route without `privateSite: 'open'` and the
//     catch-all render MembersOnlyRoute in place, at the same URL (route keys
//     stay the path, so admission swaps the real page in fresh and nothing
//     navigates). On a public league the access value is the constant `open`
//     and the elements are exactly as before. The wall lives only here, so
//     /admin and the standalone pages are never walled (T7);
//   - a Growth preview (Ops › Growth) is private too, to its lead's staff
//     session: while not admitted every path draws its own sign-in wall
//     (components/PreviewWall, the admin's sign-in look in the league's colours),
//     and once admitted the site renders as any league's, with a small bar
//     (preview/PreviewBar). Both lazy: a public league loads neither.
//
// Mid-session switches (fresh tenant, preview draft, dev override) preload
// the new template and swap inside startTransition: no loader flash.

import { lazy, startTransition, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { useLeague } from '@/contexts/LeagueContext'
import { useIsDesignPreview } from '@/contexts/PreviewBridge'
import { SectionRoute, AddonRoute } from '@/components/RouteGates'
import { addonEntitled, isPrivatePreview } from '@/contexts/tenantAccess'
import { MemberAuthProvider } from '@/contexts/MemberAuthContext'
import { SiteAccessProvider } from '../access/SiteAccessProvider'
import { isWalled, useSiteAccess } from '../access/context'
import { DEFAULT_THEME } from '@/utils/theme'
import { SITE_ROUTES, type SiteRouteDef } from '../routes'
import { SiteTemplateContext, type SiteTemplateContextValue } from './hostContext'
import { persistDevOverride, readDevOverride } from './devOverride'
import { resolveTemplate, type ResolvedTemplate } from './resolve'
import { brandDeriver, isTemplateModuleReady, loadTemplateModule, readTemplateModule } from './moduleCache'
import { ensureTemplateFonts } from './fonts'
import { MembersOnlyRoute, PageRoute } from './PageRoute'
import type { SiteTemplate, SiteTemplateManifest } from './types'

export const TEMPLATE_ATTR = 'data-site-template'

// A Growth preview's wall and bar: only a preview ever loads them.
const PreviewWall = lazy(() => import('@/components/PreviewWall'))
const PreviewBar = lazy(() => import('../preview/PreviewBar'))

function sameOptions(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const ka = Object.keys(a)
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k])
}

/** §5.1, memoised on its inputs. */
function useResolvedTemplate(): ResolvedTemplate {
  const { league } = useLeague()
  const inPreview = useIsDesignPreview()
  const { search } = useLocation()
  const siteTemplate = league?.site_template
  // `?__template=` is kept for the tab (sessionStorage) after render, not during it.
  useEffect(() => persistDevOverride(search), [search])
  return useMemo(
    () => resolveTemplate({ tenant: siteTemplate ? { site_template: siteTemplate } : null, inPreview, override: readDevOverride(search) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the options object is compared by value
    [siteTemplate?.id, JSON.stringify(siteTemplate?.options ?? null), inPreview, search],
  )
}

/** Hold the shown template; swap to a newly resolved one inside startTransition once its module is ready. */
function useShownTemplate(resolved: ResolvedTemplate): ResolvedTemplate {
  const [shown, setShown] = useState(resolved)
  useEffect(() => {
    if (resolved.manifest.id === shown.manifest.id) {
      if (!sameOptions(resolved.options, shown.options)) setShown(resolved)
      return
    }
    let cancelled = false
    const swap = () => { if (!cancelled) startTransition(() => setShown(resolved)) }
    if (isTemplateModuleReady(resolved.manifest)) swap()
    else loadTemplateModule(resolved.manifest).then(swap, () => swap())
    return () => { cancelled = true }
  }, [resolved, shown])
  return shown
}

function useHtmlTemplateAttr(id: string) {
  useLayoutEffect(() => {
    const root = document.documentElement
    root.setAttribute(TEMPLATE_ATTR, id)
    return () => root.removeAttribute(TEMPLATE_ATTR)
  }, [id])
}

/**
 * Set the template's --st-* variables on <html>. Eager templates (classic)
 * must have an empty brand() (manifest.test checks it), so they skip the
 * brand pipeline entirely and write nothing to the DOM.
 */
function useBrandVars(manifest: SiteTemplateManifest, template: SiteTemplate, options: Record<string, string | boolean>) {
  const { league } = useLeague()
  const theme = league?.theme ?? DEFAULT_THEME
  const derive = brandDeriver(manifest)
  const vars = useMemo(() => (derive ? template.brand(derive(theme), options as never) : {}), [derive, template, theme, options])
  useLayoutEffect(() => {
    const entries = Object.entries(vars)
    if (entries.length === 0) return // classic: no DOM writes at all
    const style = document.documentElement.style
    for (const [k, v] of entries) style.setProperty(k, v)
    return () => { for (const [k] of entries) style.removeProperty(k) }
  }, [vars])
}

function gate(r: SiteRouteDef) {
  const page = <PageRoute page={r.page} />
  if (r.section) return <SectionRoute section={r.section}>{page}</SectionRoute>
  if (r.addon) return <AddonRoute addon={r.addon}>{page}</AddonRoute>
  return page
}

export function SiteTemplateHost() {
  const { league } = useLeague()
  const resolved = useResolvedTemplate()
  const shown = useShownTemplate(resolved)
  const template = readTemplateModule(shown.manifest) // classic: sync; others: suspends on a cold first paint
  useHtmlTemplateAttr(shown.manifest.id)
  useBrandVars(shown.manifest, template, shown.options)
  useEffect(() => ensureTemplateFonts(shown.manifest.fonts), [shown.manifest.fonts])

  const value = useMemo<SiteTemplateContextValue>(
    () => ({ manifest: shown.manifest, template, options: shown.options }),
    [shown.manifest, template, shown.options],
  )
  // Strict add-on routes exist only while entitled (fail closed). Keys stay the
  // path, so no other route remounts when one appears. A `whenOff` route of a
  // known tenant without the add-on mounts on its off page instead.
  const routes = SITE_ROUTES.flatMap((r): SiteRouteDef[] => {
    if (!r.onlyWith || addonEntitled(league, r.onlyWith)) return [r]
    return league && r.whenOff ? [{ path: r.path, page: r.whenOff }] : []
  })

  return (
    <SiteTemplateContext.Provider value={value}>
      <MemberAuthProvider>
        <SiteAccessProvider>
          <SiteRoutes routes={routes} template={template} />
        </SiteAccessProvider>
      </MemberAuthProvider>
    </SiteTemplateContext.Provider>
  )
}

/** The route list, walled in place while a private site's visitor is not admitted. */
function SiteRoutes({ routes, template }: { routes: readonly SiteRouteDef[]; template: SiteTemplate }) {
  const access = useSiteAccess()
  const walled = isWalled(access)
  const { league } = useLeague()
  // A Growth preview: its own wall on every path (no member pages to leave
  // open), then the site with its lead's bar.
  if (isPrivatePreview(league)) {
    if (walled) return <PreviewWall />
    return (
      <>
        <RouteList routes={routes} template={template} walled={false} />
        {access.kind === 'admitted' && <PreviewBar />}
      </>
    )
  }
  return <RouteList routes={routes} template={template} walled={walled} />
}

function RouteList({ routes, template, walled }: { routes: readonly SiteRouteDef[]; template: SiteTemplate; walled: boolean }) {
  const { NotFound } = template.states
  const { Frame } = template.shell
  return (
    <Routes>
      {/* Elements are created per render, like App.tsx's route list today,
          so pages re-render exactly when they did before. */}
      {routes.map((r) => <Route key={r.path} path={r.path} element={walled && r.privateSite !== 'open' ? <MembersOnlyRoute /> : gate(r)} />)}
      <Route path="*" element={walled ? <MembersOnlyRoute /> : Frame ? <Frame page="notFound"><NotFound /></Frame> : <NotFound />} />
    </Routes>
  )
}

export default SiteTemplateHost
